import { isUFBRequest, resolveOriginHost } from '../../api/request-helpers';

// timeout consts
export const TIMEOUT = 20000;
export const MAX_TIMEOUT = 60000;

// http header consts
export const ACCEPT_LANGUAGE_HEADER_KEY = 'accept-language';
export const FAILED_PERMISSION_HEADER_KEY = 'x-udemy-failed-permission';
export const FAILED_PERMISSION_HEADER_VALUE = 'user-auth';
export const REQUESTED_WITH_HEADER_KEY = 'x-requested-with';
export const REQUESTED_WITH_HEADER_VALUE = 'nextjs-app-context';
export const UDEMY_REQUEST_CONTEXT_HEADER = 'x-udemy-request-context';

// In Node.js versions 19 and later, the fetch API defaults to using HTTP keep-alive.
// This means that after a request is completed, the TCP connection remains open for a period (typically 5 seconds)
// to allow for subsequent requests to the same server without re-establishing the connection.
// This behavior can improve performance by reducing the overhead associated with establishing new connections for each request.
let KEEP_ALIVE = true;
/**
 * setKeepAlive()
 * Used to change keep alive behavior globally.  Should be used for testing purposes only.
 * @param keepAlive
 */
export function setKeepAlive(keepAlive) {
  KEEP_ALIVE = keepAlive;
}

/**
 * defaultStaticOptions()
 *
 * Returns default options for upstream fetch for static requests
 *
 * @returns {RequestInit} - fetch options
 */
export function defaultStaticOptions() {
  const defaultOptions = {
    credentials: 'include',
    headers: {}
  };
  if (KEEP_ALIVE === false) {
    defaultOptions.headers = {
      connection: 'close'
    };
  }
  return defaultOptions;
}

/**
 * defaultUserSpecificOptions()
 *
 * Returns default options for upstream fetch for user-specific requests
 *
 * @returns {RequestInit} - fetch options
 */
/*
export function defaultUserSpecificOptions(): RequestInit {
    return {
        credentials: 'include',
        cache: 'no-cache',
    };
}
*/

/**
 * hasInvalidAuthHeader()
 *
 * Returns whether or not given HTTP request has failed permission header
 *
 * @param req {NextApiRequest} - HTTP request
 * @returns {boolean} - true if failed permission header is found
 */
export function hasInvalidAuthHeader(req) {
  const headers = req.headers;
  return headers[FAILED_PERMISSION_HEADER_KEY] === FAILED_PERMISSION_HEADER_VALUE;
}

/**
 * getDjangoHttpCachingHeaders()
 *
 * Gets headers that are used with Django HTTP caching framework: "udemy.utils.http_cache"
 * See https://github.com/udemy/website-django/tree/master/udemy/utils/http_cache for details
 *
 * @param req {NextApiRequest} - HTTP request
 * @returns {Record<string, string>} - headers
 */

export function getDjangoHttpCachingHeaders(req) {
  const cookies = req.cookies;

  /*
   * While the backend can disable client caching for any further
   * requests, the client can ensure that previously-cached requests
   * are cleared by disabling cache-identification headers if the
   * cache override cookie is set.
   */
  if (cookies.ud_client_cache_off) {
    return {};
  }

  /*
   * Our client caching layer needs to transfer "cache identifiers"
   * between client & server as follows:
   * 1. Server sends "cache identifiers" in the form of cookies
   *    named "ud_cache_id_name"
   * 2. Client should return these identifiers as headers in the
   *    format "X-Udemy-Cache-Id-Name"
   *
   * This code follows this specification to enable client-side caching.
   * See details inside udemy/utils/http_cache/README.md
   */
  const headers = {};
  Object.entries(cookies).filter(entry => entry[0].startsWith('ud_cache_')).forEach(entry => {
    const suffix = entry[0].split('ud_cache_')[1];
    const name = suffix.split('_').map(s => s.slice(0, 1).toUpperCase() + s.slice(1)).join('-');
    const key = `X-Udemy-Cache-${name}`;
    headers[key] = String(entry[1]);
  });
  return headers;
}

/**
 * populateAcceptLanguageHeader()
 *
 * This code has been duplicated quite a few times.
 *
 * @param req {NextApiRequest} - HTTP request
 * @returns {Record<string, string>} - headers
 */
export function getAcceptLanguageHeader(req) {
  const cookies = req.cookies;
  if (cookies.ud_locale) {
    const udLocaleConverted = cookies.ud_locale.split('_').join('-');
    return {
      [ACCEPT_LANGUAGE_HEADER_KEY]: udLocaleConverted
    };
  }
}

/**
 * getBaseUrl()
 *
 * Computes a `baseUrl()` based on incoming request and a given upstreamRemote
 * If the request has a `x-udemy-origin-host` header, and its a UB url, then use that.
 *
 * @param req
 * @param upstreamHost
 * @returns
 */
export function getBaseUrl(req, upstreamHost) {
  const udemyOriginHost = resolveOriginHost(req);
  if (udemyOriginHost && isUFBRequest(req)) {
    return `https://${udemyOriginHost}`;
  }
  return upstreamHost;
}

/**
 * forwardUserSpecificHeaders()
 *
 * Forwards HTTP headers from a request to upstream request.
 *
 * @param req {NextApiRequest} - HTTP request
 * @returns {HeadersInit} - headers
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function forwardHeaders(req) {
  const udApiHeaders = {
    [REQUESTED_WITH_HEADER_KEY]: REQUESTED_WITH_HEADER_VALUE,
    ...getDjangoHttpCachingHeaders(req),
    ...getAcceptLanguageHeader(req)
  };

  // Get all headers that need to be copied forward
  const forwardHeadersList = ['x-cdn', 'x-udemy', 'cache-control', 'referer', 'cookie'];
  const forwardHeaders = Object.entries(req.headers).map(([k, v]) => {
    const lowerCaseKey = k.toLowerCase();
    return forwardHeadersList.some(forwardHeadersKey => lowerCaseKey.startsWith(forwardHeadersKey)) ? [lowerCaseKey, v] : ['', ''];
  }).filter(([key]) => !!key)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  .reduce((result, [key, value]) => {
    result[key] = value;
    return result;
  }, {});

  // Forward an authorization header for compatibility
  if (req.cookies.access_token) {
    forwardHeaders.authorization = `Bearer ${req.cookies.access_token}`;
  }
  return {
    ...forwardHeaders,
    ...udApiHeaders
  };
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const merge = require('deepmerge'); // https://github.com/TehShrike/deepmerge#include

/**
 * mergeWithArrayOverwrite()
 * Deep merge with array overwrite behavior.  Mainly used to merge UDData
 * @param args - array of objects to merge
 * @returns
 */
export function mergeWithArrayOverwrite(...args) {
  const arrayMerge = (_destinationArray, sourceArray) => sourceArray;
  return merge(...args, {
    arrayMerge
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertUndefinedToNull(obj) {
  if (obj === undefined) return null;
  if (obj === null || typeof obj !== 'object') return obj;
  Object.keys(obj).forEach(key => {
    obj[key] = convertUndefinedToNull(obj[key]);
  });
  return obj;
}
//# sourceMappingURL=utils.js.map